import styled from '@emotion/styled'
import { OrderItem } from 'shop/types/cart'
import Quantity from '../Quantity/Quantity'
import Price from '../Price/Price'
import { ModifierList } from '../Modifiers'
import { OptionList } from '../Options'
import { Name } from '../commonStyles'
import { CartType } from '../Cart'
import QuantityControl from 'shop/components/QuantityControl/QuantityControl'
import { StyledHTMLElement } from 'shop/theme/types'
import { StyledBinIcon } from 'shop/components/Shop/commonIcons'

interface Props {
  orderItem: OrderItem
  type: CartType
  onQuantityChange?: (orderItem: OrderItem, delta: number) => Promise<void>
  isLast?: boolean
  pendingDeltas?: Record<string, number>
}

const CartItem = ({
  orderItem,
  type,
  onQuantityChange,
  isLast = false,
  pendingDeltas = {}
}: Props) => {
  const {
    name,
    quantity,
    total: itemTotal,
    modifierGroups,
    options
  } = orderItem
  const isShopCart = type === CartType.Shop
  const pendingDelta = pendingDeltas[orderItem.id] || 0
  const displayQuantity = quantity + pendingDelta
  const isPendingRemoval = displayQuantity <= 0

  /** Call quantity change callback with orderItem and new quantity value */
  const handleQuantityChange = (delta: number) => {
    if (onQuantityChange) {
      onQuantityChange(orderItem, delta)
    }
  }

  const detailedContent = (
    <DetailedContent data-testid='cart-item-detailed' isShopCart={isShopCart}>
      {!!options && <OptionList options={options} />}
      {!!modifierGroups.length && (
        <ModifierList modifierGroups={modifierGroups} />
      )}
    </DetailedContent>
  )

  return (
    <ContentWrapper
      isLast={isLast}
      isShopCart={isShopCart}
      isPendingRemoval={isPendingRemoval}
    >
      <BasicContent data-testid='cart-item-basic' isShopCart={isShopCart}>
        {!isShopCart && <Quantity quantity={quantity} />}
        <ProductName isShopCart={isShopCart}>{name}</ProductName>
        {!isShopCart && <Price price={itemTotal}></Price>}
      </BasicContent>
      {detailedContent}
      {isShopCart && (
        <ControlContent>
          <Price
            type={isShopCart ? 'horizontal' : 'vertical'}
            price={itemTotal}
          ></Price>
          <QuantityControl
            quantity={displayQuantity}
            width={'32px'}
            onIncrement={() => handleQuantityChange(1)}
            onDecrement={() => handleQuantityChange(-1)}
            customDecrementIcon={
              displayQuantity === 1 ? <StyledBinIcon /> : undefined
            }
          />
        </ControlContent>
      )}
    </ContentWrapper>
  )
}

const ContentWrapper = styled.li<
  StyledHTMLElement & {
    isLast: boolean
    isShopCart: boolean
    isPendingRemoval: boolean
  }
>(({ isLast, isShopCart, isPendingRemoval }) => {
  return {
    display: isShopCart ? 'block' : 'grid',
    gridTemplateColumns: isShopCart ? undefined : '38px 1fr 75px',
    marginBottom: isLast && isShopCart ? '0px' : '20px',
    listStyle: 'none',
    transition: 'all 0.3s ease',
    opacity: 1,
    transform: 'translateY(0)',
    maxHeight: '200px',
    overflow: 'hidden',
    ...(isPendingRemoval && {
      opacity: 0,
      transform: 'translateY(-10px)',
      maxHeight: '0px'
    })
  }
})

const BasicContent = styled.div<StyledHTMLElement & { isShopCart: boolean }>(
  ({ isShopCart }) => {
    return {
      marginBottom: isShopCart ? '6px' : '0',
      display: isShopCart ? 'block' : 'contents',
      fontWeight: 500
    }
  }
)

const DetailedContent = styled.div<StyledHTMLElement & { isShopCart: boolean }>(
  ({ isShopCart }) => {
    return {
      marginBottom: isShopCart ? '8px' : '0',
      'p, span': {
        fontSize: '14px'
      },
      maxWidth: '82%',
      gridColumn: '2 / -1'
    }
  }
)

const ControlContent = styled.div(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center'
}))

const ProductName = styled(Name)<{ isShopCart: boolean }>(
  ({ theme, isShopCart }) => ({
    fontSize: '16px',
    margin: '0',
    textOverflow: 'ellipsis',
    whiteSpace: isShopCart ? 'normal' : 'nowrap',
    overflow: 'hidden',
    fontWeight: 600,
    [theme.mediaQueries.viewport6]: {
      whiteSpace: 'nowrap'
    }
  })
)

export default CartItem
